import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useWallet } from "@/contexts/WalletContext";
import { useWallet as useSolanaWallet, useConnection } from "@solana/wallet-adapter-react";
import { toast } from "@/hooks/use-toast";
import { Rocket, Upload, Info, Sparkles, Target, Users, Loader2, CheckCircle, XCircle, ExternalLink, RotateCcw, Copy, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { INITIAL_TOKEN_SUPPLY } from "@/lib/bonding-curve";
import { createToken } from "@/lib/solana-api";
import { buildTokenCreationTransaction } from "@/lib/solana-transactions";
import { createPendingTransaction, useTransactionMonitor } from "@/hooks/use-transaction-status";
import { uploadTokenMetadata } from "@/lib/metadata-uploader";
import { PublicKey } from "@solana/web3.js";
import { findTokenConfigPDA, findPoolPDA, PROGRAM_IDS } from "@/lib/anchor/idl-types";
import { validateAllPrograms } from "@/lib/anchor/validation";

export default function CreateCoin() {
  const navigate = useNavigate();
  const { connected, publicKey, connect } = useWallet();
  const { signTransaction } = useSolanaWallet();
  const { connection } = useConnection();
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [step, setStep] = useState<"form" | "creating" | "success">("form");
  const [createdCoin, setCreatedCoin] = useState<{
    coinId: string;
    mintAddress?: string;
    explorerUrl?: string;
  } | null>(null);
  const [transactionSignature, setTransactionSignature] = useState<string | null>(null);
  const [deploymentStep, setDeploymentStep] = useState<string>("");
  const [retryCount, setRetryCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<number | null>(null);
  const [stepStatuses, setStepStatuses] = useState<Record<string, 'pending' | 'active' | 'completed' | 'failed'>>({});
  const { status: txStatus } = useTransactionMonitor(transactionSignature || undefined);
  
  const [form, setForm] = useState({
    name: "",
    ticker: "",
    description: "",
    image: "",
    targetMarketCap: 69000,
    isPremium: false,
    // New customization options
    lockBranding: false,
    enableAds: false,
    lockTransfers: false,
    customWebsite: "",
    customTwitter: "",
    customTelegram: "",
  });

  // Fee calculation - CHEAPER THAN PUMP.FUN (0.02 SOL base vs their 0.05)
  const baseFee = 0.02; // Base creation fee
  const brandingFee = form.lockBranding ? 0.1 : 0;
  const adsFee = form.enableAds ? 0.05 : 0;
  const lockFee = form.lockTransfers ? 0.15 : 0;
  const premiumFee = form.isPremium ? 0.3 : 0;
  const totalFee = baseFee + brandingFee + adsFee + lockFee + premiumFee;

  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateProgress = (step: string, status: 'pending' | 'active' | 'completed' | 'failed', progressPercent: number, estimatedTime?: number) => {
    setDeploymentStep(step);
    setStepStatuses(prev => ({ ...prev, [step]: status }));
    setProgress(progressPercent);
    if (estimatedTime !== undefined) {
      setEstimatedTimeRemaining(estimatedTime);
    }
  };

  const buildAndSubmitTransaction = async (metadataURI: string) => {
    const totalSteps = 7;
    let currentStep = 0;

    try {
      // Step 1: Generating token mint
      currentStep = 1;
      updateProgress("Generating token mint...", "active", (currentStep - 1) / totalSteps * 100, 25);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate time

      // Build token creation transaction
      const { transaction, mintKeypair } = await buildTokenCreationTransaction(
        connection,
        { publicKey: new PublicKey(publicKey), signTransaction },
        {
          name: form.name.trim(),
          symbol: form.ticker.toUpperCase(),
          uri: metadataURI,
          decimals: 9,
          totalSupply: BigInt(INITIAL_TOKEN_SUPPLY),
          targetMarketCap: BigInt(form.targetMarketCap * 1e9), // Convert to lamports
          creator: new PublicKey(publicKey),
        }
      );
      updateProgress("Generating token mint...", "completed", currentStep / totalSteps * 100, 20);

      // Step 2: Requesting wallet signature
      currentStep = 2;
      updateProgress("Requesting wallet signature...", "active", (currentStep - 1) / totalSteps * 100, 15);
      // Sign transaction
      const signedTx = await signTransaction(transaction);
      updateProgress("Requesting wallet signature...", "completed", currentStep / totalSteps * 100, 10);

      // Step 3: Submitting transaction
      currentStep = 3;
      updateProgress("Submitting transaction...", "active", (currentStep - 1) / totalSteps * 100, 8);
      // Send transaction
      const signature = await connection.sendRawTransaction(signedTx.serialize());
      setTransactionSignature(signature);
      updateProgress("Submitting transaction...", "completed", currentStep / totalSteps * 100, 5);

      // Step 4: Create pending transaction record
      currentStep = 4;
      updateProgress("Creating transaction record...", "active", (currentStep - 1) / totalSteps * 100, 3);
      await createPendingTransaction(
        `temp-${Date.now()}`, // Temporary coinId until confirmed
        "buy", // This is a creation, but we'll use buy for now
        0, // No amount for creation
        0, // No price for creation
        signature
      );
      updateProgress("Creating transaction record...", "completed", currentStep / totalSteps * 100, 2);

      // Step 5: Confirming on blockchain
      currentStep = 5;
      updateProgress("Confirming on blockchain...", "active", (currentStep - 1) / totalSteps * 100, 10);
      // Wait for confirmation
      await connection.confirmTransaction(signature, "confirmed");
      updateProgress("Confirming on blockchain...", "completed", currentStep / totalSteps * 100, 5);

      // Step 6: Initializing bonding curve
      currentStep = 6;
      updateProgress("Initializing bonding curve...", "active", (currentStep - 1) / totalSteps * 100, 3);
      // Generate coinId (this would normally come from the edge function)
      const coinId = mintKeypair.publicKey.toBase58();
      updateProgress("Initializing bonding curve...", "completed", currentStep / totalSteps * 100, 2);

      // Step 7: Storing metadata
      currentStep = 7;
      updateProgress("Storing metadata...", "active", (currentStep - 1) / totalSteps * 100, 5);
      // Call simplified edge function to store metadata
      const result = await createToken({
        coinId,
        signature,
        mintAddress: mintKeypair.publicKey.toBase58(),
        name: form.name.trim(),
        ticker: form.ticker.toUpperCase(),
        description: form.description.trim() || undefined,
        image: form.image || undefined,
        targetMarketCap: form.targetMarketCap,
        isPremium: form.isPremium,
        // Skip blockchain deployment since we did it client-side
      });

      if (!result.success) {
        updateProgress("Storing metadata...", "failed", currentStep / totalSteps * 100);
        throw new Error(result.error || "Failed to store metadata");
      }

      updateProgress("Storing metadata...", "completed", 100, 0);

      setCreatedCoin({
        coinId: result.coinId,
        mintAddress: mintKeypair.publicKey.toBase58(),
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`,
      });

      setStep("success");

      toast({
        title: "Token Deployed Successfully!",
        description: `${form.name} ($${form.ticker}) deployed to Solana blockchain!`,
      });

      // Navigate to coin page after brief delay
      setTimeout(() => {
        navigate(`/coin/${result.coinId}`);
      }, 3000);
    } catch (error) {
      // Mark current step as failed
      updateProgress(deploymentStep, "failed", progress);
      throw error;
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    } else if (form.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    } else if (form.name.length > 32) {
      newErrors.name = "Name must be less than 32 characters";
    } else {
      newErrors.name = '';
    }

    if (!form.ticker.trim()) {
      newErrors.ticker = "Ticker is required";
    } else if (form.ticker.length < 2) {
      newErrors.ticker = "Ticker must be at least 2 characters";
    } else if (form.ticker.length > 10) {
      newErrors.ticker = "Ticker must be less than 10 characters";
    } else if (!/^[A-Z0-9]+$/.test(form.ticker)) {
      newErrors.ticker = "Ticker must be alphanumeric";
    } else {
      newErrors.ticker = '';
    }

    if (form.targetMarketCap < 1000) {
      newErrors.targetMarketCap = "Target must be at least $1,000";
    } else if (form.targetMarketCap > 10000000) {
      newErrors.targetMarketCap = "Target must be less than $10,000,000";
    } else {
      newErrors.targetMarketCap = '';
    }

    if (form.description && form.description.length > 500) {
      newErrors.description = "Description must be less than 500 characters";
    } else {
      newErrors.description = '';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).filter(k => newErrors[k]).length === 0;
  };

  useEffect(() => {
    validateForm();
  }, [form]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({ title: "File too large", description: "Image must be less than 2MB", variant: "destructive" });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result as string;
        setImagePreview(result);
        setForm((f) => ({ ...f, image: result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!connected || !publicKey || !signTransaction || !connection) {
      connect();
      return;
    }

    if (!validateForm()) {
      toast({ title: "Validation Error", description: "Please fix the errors below", variant: "destructive" });
      return;
    }

    // Strict on-chain enforcement: Validate program deployments before proceeding
    try {
      await validateAllPrograms(connection);
    } catch (error) {
      console.error("Program deployment validation failed:", error);
      toast({
        title: "On-Chain Programs Not Deployed",
        description: "Required Solana programs are not deployed. Contact administrator.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setStep("creating");
    setProgress(0);
    setEstimatedTimeRemaining(30);
    setStepStatuses({});
    setDeploymentStep("Generating token mint...");
    setRetryCount(0);
    setErrorMessage(null);

    try {
      setDeploymentStep("Uploading metadata...");

      // Upload token metadata
      let metadataURI: string;
      try {
        metadataURI = await uploadTokenMetadata({
          name: form.name.trim(),
          symbol: form.ticker.toUpperCase(),
          description: form.description.trim() || "",
          image: form.image || "",
        });
      } catch (uploadError) {
        console.warn("Metadata upload failed, using fallback:", uploadError);
        // Fallback to data URI
        const metadataJson = JSON.stringify({
          name: form.name.trim(),
          symbol: form.ticker.toUpperCase(),
          description: form.description.trim() || "",
          image: form.image || "",
        });
        metadataURI = `data:application/json;base64,${btoa(metadataJson)}`;
      }

      await buildAndSubmitTransaction(metadataURI);

    } catch (error) {
      console.error("Create coin error:", error);

      let errorMessage = "Failed to create coin. Please try again.";
      let isRetryable = true;

      if (error instanceof Error) {
        if (error.message.includes("User rejected")) {
          errorMessage = "Transaction cancelled by user.";
          isRetryable = false;
        } else if (error.message.includes("insufficient")) {
          errorMessage = `Insufficient SOL balance. Required: ${totalFee} SOL`;
          isRetryable = false;
        } else if (error.message.includes("timeout") || error.message.includes("RPC")) {
          errorMessage = "Transaction timed out. Please try again.";
          isRetryable = true;
        } else {
          errorMessage = error.message;
        }
      }

      if (isRetryable) {
        setErrorMessage(errorMessage);
        setRetryCount(prev => prev + 1);
      } else {
        setStep("form");
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async () => {
    setErrorMessage(null);
    setProgress(0);
    setEstimatedTimeRemaining(30);
    setStepStatuses({});
    setDeploymentStep("Rebuilding transaction...");
    setIsLoading(true);

    try {
      // Re-upload metadata (or reuse, but for simplicity re-upload)
      let metadataURI: string;
      try {
        metadataURI = await uploadTokenMetadata({
          name: form.name.trim(),
          symbol: form.ticker.toUpperCase(),
          description: form.description.trim() || "",
          image: form.image || "",
        });
      } catch (uploadError) {
        console.warn("Metadata upload failed, using fallback:", uploadError);
        const metadataJson = JSON.stringify({
          name: form.name.trim(),
          symbol: form.ticker.toUpperCase(),
          description: form.description.trim() || "",
          image: form.image || "",
        });
        metadataURI = `data:application/json;base64,${btoa(metadataJson)}`;
      }

      await buildAndSubmitTransaction(metadataURI);

    } catch (error) {
      console.error("Retry error:", error);

      let errorMessage = "Retry failed. Please try again.";
      let isRetryable = true;

      if (error instanceof Error) {
        if (error.message.includes("User rejected")) {
          errorMessage = "Transaction cancelled by user.";
          isRetryable = false;
        } else if (error.message.includes("insufficient")) {
          errorMessage = `Insufficient SOL balance. Required: ${totalFee} SOL`;
          isRetryable = false;
        } else if (error.message.includes("timeout") || error.message.includes("RPC")) {
          errorMessage = "Transaction timed out. Please try again.";
          isRetryable = true;
        } else {
          errorMessage = error.message;
        }
      }

      if (isRetryable && retryCount < 3) {
        setErrorMessage(errorMessage);
        setRetryCount(prev => prev + 1);
      } else {
        setStep("form");
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (step === "creating") {
    const steps = [
      "Generating token mint...",
      "Uploading metadata...",
      "Requesting wallet signature...",
      "Submitting transaction...",
      "Creating transaction record...",
      "Confirming on blockchain...",
      "Initializing bonding curve...",
      "Storing metadata..."
    ];

    const getStepStatus = (stepName: string) => {
      return stepStatuses[stepName] || "pending";
    };

    const getStepNumber = (stepName: string) => {
      return steps.indexOf(stepName) + 1;
    };

    return (
      <>
        <Helmet>
          <title>Deploying Token... - TheMoonRun</title>
        </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-lg px-4">
            <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">Deploying Your Token</h2>
            <p className="text-muted-foreground mb-4">{deploymentStep}</p>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Progress</span>
                <span className="text-sm font-mono text-foreground">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="w-full" />
              {estimatedTimeRemaining !== null && (
                <div className="flex items-center justify-center gap-1 mt-2 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  <span>~{estimatedTimeRemaining}s remaining</span>
                </div>
              )}
            </div>

            {errorMessage && (
              <div className="mt-4 p-4 rounded-xl bg-destructive/10 border border-destructive/20">
                <p className="text-sm font-medium text-destructive mb-1">Transaction Failed</p>
                <p className="text-sm text-destructive/80">{errorMessage}</p>
              </div>
            )}

            {/* Deployment Status Steps */}
            <div className="mt-6 space-y-3">
              {steps.map((stepName) => {
                const status = getStepStatus(stepName);
                const stepNumber = getStepNumber(stepName);
                return (
                  <div key={stepName} className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold">
                      {status === "completed" ? (
                        <div className="w-8 h-8 rounded-full bg-success flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      ) : status === "failed" ? (
                        <div className="w-8 h-8 rounded-full bg-destructive flex items-center justify-center">
                          <XCircle className="w-4 h-4 text-white" />
                        </div>
                      ) : status === "active" ? (
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 text-primary animate-spin" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-secondary border border-border flex items-center justify-center">
                          <span className="text-muted-foreground font-mono text-xs">{stepNumber}</span>
                        </div>
                      )}
                    </div>
                    <span className={cn(
                      "text-sm",
                      status === "completed" ? "text-success" :
                      status === "failed" ? "text-destructive" :
                      status === "active" ? "text-foreground" :
                      "text-muted-foreground"
                    )}>
                      {stepNumber}/{steps.length}: {stepName}
                    </span>
                  </div>
                );
              })}
            </div>

            {transactionSignature && (
              <div className="mt-6 p-4 rounded-xl bg-card border border-border">
                <p className="text-xs text-muted-foreground mb-2">Transaction Signature</p>
                <p className="font-mono text-sm text-foreground break-all">{transactionSignature}</p>
                <div className="mt-2 flex justify-end">
                  <a
                    href={`https://explorer.solana.com/tx/${transactionSignature}?cluster=devnet`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View on Explorer
                  </a>
                </div>
              </div>
            )}

            {retryCount > 0 && retryCount < 3 && (
              <div className="mt-6">
                <Button
                  onClick={handleRetry}
                  variant="outline"
                  className="w-full"
                  disabled={isLoading}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Retry Transaction ({retryCount}/3)
                </Button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  if (step === "success") {
    return (
      <>
        <Helmet>
          <title>Token Deployed! - TheMoonRun</title>
        </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-lg px-4">
            <div className="w-20 h-20 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              {form.name} Deployed Successfully!
            </h2>
            <p className="text-muted-foreground mb-6">
              Your token ${form.ticker} is now live on Solana blockchain
            </p>

            {/* Deployment Summary */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-3 rounded-lg bg-card border border-border">
                <span className="text-sm text-muted-foreground">Token Standard</span>
                <span className="font-mono text-sm">SPL</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-card border border-border">
                <span className="text-sm text-muted-foreground">Blockchain</span>
                <span className="font-mono text-sm">Solana</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-card border border-border">
                <span className="text-sm text-muted-foreground">Deployment</span>
                <span className="font-mono text-sm text-success">Confirmed</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-card border border-border">
                <span className="text-sm text-muted-foreground">Target Market Cap</span>
                <span className="font-mono text-sm">${form.targetMarketCap.toLocaleString()}</span>
              </div>
            </div>

            {/* Mint Address */}
            {createdCoin?.mintAddress && (
              <div className="p-4 rounded-xl bg-card border border-border mb-4 text-left">
                <p className="text-xs text-muted-foreground mb-2">Token Mint Address</p>
                <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg mb-2">
                  <code className="text-sm font-mono flex-1 break-all">{createdCoin.mintAddress}</code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(createdCoin.mintAddress || "");
                      toast({ title: "Copied!", description: "Mint address copied to clipboard" });
                    }}
                    aria-label="Copy mint address"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex justify-end">
                  <a
                    href={`https://explorer.solana.com/address/${createdCoin.mintAddress}?cluster=devnet`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View on Explorer
                  </a>
                </div>
              </div>
            )}

            {/* PDAs */}
            {createdCoin?.mintAddress && (
              <div className="grid grid-cols-1 gap-3 mb-4">
                <div className="p-4 rounded-xl bg-card border border-border text-left">
                  <p className="text-xs text-muted-foreground mb-2">Token Config PDA</p>
                  <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg mb-2">
                    <code className="text-sm font-mono flex-1 break-all">
                      {(() => {
                        const mintPubkey = new PublicKey(createdCoin.mintAddress);
                        const [tokenConfigPda] = findTokenConfigPDA(mintPubkey, new PublicKey(PROGRAM_IDS.TOKEN));
                        return tokenConfigPda.toBase58();
                      })()}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const mintPubkey = new PublicKey(createdCoin.mintAddress);
                        const [tokenConfigPda] = findTokenConfigPDA(mintPubkey, new PublicKey(PROGRAM_IDS.TOKEN));
                        navigator.clipboard.writeText(tokenConfigPda.toBase58());
                        toast({ title: "Copied!", description: "Token Config PDA copied to clipboard" });
                      }}
                      aria-label="Copy token config PDA"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-card border border-border text-left">
                  <p className="text-xs text-muted-foreground mb-2">Pool PDA</p>
                  <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg mb-2">
                    <code className="text-sm font-mono flex-1 break-all">
                      {(() => {
                        const mintPubkey = new PublicKey(createdCoin.mintAddress);
                        const [poolPda] = findPoolPDA(mintPubkey, new PublicKey(PROGRAM_IDS.BONDING_CURVE));
                        return poolPda.toBase58();
                      })()}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const mintPubkey = new PublicKey(createdCoin.mintAddress);
                        const [poolPda] = findPoolPDA(mintPubkey, new PublicKey(PROGRAM_IDS.BONDING_CURVE));
                        navigator.clipboard.writeText(poolPda.toBase58());
                        toast({ title: "Copied!", description: "Pool PDA copied to clipboard" });
                      }}
                      aria-label="Copy pool PDA"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Initial Pool Reserves */}
            <div className="p-4 rounded-xl bg-card border border-border mb-4 text-left">
              <p className="text-xs text-muted-foreground mb-3">Initial Pool Reserves</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-secondary/50 rounded-lg">
                  <p className="text-lg font-bold text-primary">0.5 SOL</p>
                  <p className="text-xs text-muted-foreground">Liquidity</p>
                </div>
                <div className="text-center p-3 bg-secondary/50 rounded-lg">
                  <p className="text-lg font-bold text-primary">800M</p>
                  <p className="text-xs text-muted-foreground">Tokens</p>
                </div>
              </div>
            </div>

            {/* Transaction Signature */}
            {transactionSignature && (
              <div className="p-4 rounded-xl bg-card border border-border mb-4 text-left">
                <p className="text-xs text-muted-foreground mb-2">Transaction Signature</p>
                <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg mb-2">
                  <code className="text-sm font-mono flex-1 break-all">{transactionSignature}</code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(transactionSignature);
                      toast({ title: "Copied!", description: "Transaction signature copied to clipboard" });
                    }}
                    aria-label="Copy transaction signature"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex justify-end gap-2">
                  <a
                    href={`https://explorer.solana.com/tx/${transactionSignature}?cluster=devnet`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Devnet Explorer
                  </a>
                  <a
                    href={`https://explorer.solana.com/tx/${transactionSignature}?cluster=mainnet-beta`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Mainnet Explorer
                  </a>
                </div>
              </div>
            )}

            {/* Success Message */}
            <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/10 mb-6">
              <p className="text-sm font-medium text-primary mb-2">🎉 Deployment Complete!</p>
              <p className="text-xs text-muted-foreground">
                Your token has been successfully deployed to the Solana blockchain with fair bonding curve mechanics.
                You can now start trading and sharing your token with the community.
              </p>
            </div>

            {/* Start Trading Button */}
            <Button
              onClick={() => navigate(`/coin/${createdCoin?.coinId}`)}
              variant="hero"
              size="lg"
              className="w-full mb-4"
            >
              <Rocket className="w-5 h-5 mr-2" />
              Start Trading
            </Button>

            <p className="text-sm text-muted-foreground">
              Your token page will be available at{" "}
              <code className="text-xs bg-secondary px-1 py-0.5 rounded">
                /coin/{createdCoin?.coinId}
              </code>
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Create Coin - TheMoonRun</title>
        <meta name="description" content="Launch your own meme coin on Solana with fair bonding curve mechanics. No presales, no rugs." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="flex">
          <Sidebar />
          
          <main className="flex-1 min-w-0 p-4 lg:p-8">
            <div className="max-w-2xl mx-auto">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
                  <Rocket className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-3xl font-display font-bold text-foreground mb-2">
                  Launch Your Coin
                </h1>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Create a fair-launch meme coin with automatic bonding curve pricing. No presales, no team allocation.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="p-6 rounded-xl bg-card border border-border space-y-6">
                  {/* Image Upload */}
                  <div>
                    <Label className="mb-2 block">Coin Image</Label>
                    <div className="flex items-start gap-4">
                      <label className={cn(
                        "w-24 h-24 rounded-xl border-2 border-dashed cursor-pointer transition-all flex items-center justify-center overflow-hidden",
                        imagePreview ? "border-primary" : "border-border hover:border-primary/50"
                      )}>
                        {imagePreview ? (
                          <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <Upload className="w-8 h-8 text-muted-foreground" />
                        )}
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                      <div className="flex-1">
                        <p className="text-sm text-muted-foreground">
                          Upload a square image for your coin. PNG, JPG up to 2MB.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Name & Ticker */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="mb-2 block">Coin Name *</Label>
                      <Input
                        id="name"
                        placeholder="e.g., Moon Doge"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        maxLength={32}
                        className={errors.name ? "border-destructive" : ""}
                        aria-describedby="name-error"
                      />
                      {errors.name !== undefined && (
                        <p id="name-error" className={`text-xs mt-1 flex items-center gap-1 ${errors.name ? 'text-destructive' : 'text-success'}`}>
                          {errors.name ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                          {errors.name || 'Valid'}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="ticker" className="mb-2 block">Ticker Symbol *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="ticker"
                          placeholder="e.g., MDOGE"
                          value={form.ticker}
                          onChange={(e) => setForm((f) => ({ ...f, ticker: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") }))}
                          className={cn("pl-7 uppercase", errors.ticker && "border-destructive")}
                          maxLength={10}
                          aria-describedby="ticker-error"
                        />
                      </div>
                      {errors.ticker !== undefined && (
                        <p id="ticker-error" className={`text-xs mt-1 flex items-center gap-1 ${errors.ticker ? 'text-destructive' : 'text-success'}`}>
                          {errors.ticker ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                          {errors.ticker || 'Valid'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <Label htmlFor="description" className="mb-2 block">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Tell the world about your coin..."
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      rows={4}
                      maxLength={500}
                      className={errors.description ? "border-destructive" : ""}
                      aria-describedby="description-error"
                    />
                    <p className="text-xs text-muted-foreground mt-1 text-right">
                      {form.description.length}/500
                    </p>
                    {errors.description !== undefined && (
                      <p id="description-error" className={`text-xs flex items-center gap-1 ${errors.description ? 'text-destructive' : 'text-success'}`}>
                        {errors.description ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                        {errors.description || 'Valid'}
                      </p>
                    )}
                  </div>

                  {/* Target Market Cap */}
                  <div>
                    <Label htmlFor="target" className="mb-2 block">Target Market Cap (USD)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="target"
                        type="number"
                        placeholder="69000"
                        value={form.targetMarketCap}
                        onChange={(e) => setForm((f) => ({ ...f, targetMarketCap: parseInt(e.target.value) || 0 }))}
                        className={cn("pl-7", errors.targetMarketCap && "border-destructive")}
                        min={1000}
                        max={10000000}
                        aria-describedby="target-error"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      When this target is reached, the coin will unlock for DEX trading
                    </p>
                    {errors.targetMarketCap !== undefined && (
                      <p id="target-error" className={`text-xs flex items-center gap-1 ${errors.targetMarketCap ? 'text-destructive' : 'text-success'}`}>
                        {errors.targetMarketCap ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                        {errors.targetMarketCap || 'Valid'}
                      </p>
                    )}
                  </div>

                  {/* Social Links */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="website" className="mb-2 block">Website</Label>
                      <Input
                        id="website"
                        placeholder="https://..."
                        value={form.customWebsite}
                        onChange={(e) => setForm((f) => ({ ...f, customWebsite: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="twitter" className="mb-2 block">Twitter</Label>
                      <Input
                        id="twitter"
                        placeholder="@username"
                        value={form.customTwitter}
                        onChange={(e) => setForm((f) => ({ ...f, customTwitter: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="telegram" className="mb-2 block">Telegram</Label>
                      <Input
                        id="telegram"
                        placeholder="t.me/..."
                        value={form.customTelegram}
                        onChange={(e) => setForm((f) => ({ ...f, customTelegram: e.target.value }))}
                      />
                    </div>
                  </div>

                  {/* Customization Options */}
                  <div className="space-y-3">
                    <Label className="text-foreground">Token Customization</Label>
                    
                    {/* Lock Branding */}
                    <label className={cn(
                      "flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all",
                      form.lockBranding ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                    )}>
                      <input
                        type="checkbox"
                        checked={form.lockBranding}
                        onChange={(e) => setForm((f) => ({ ...f, lockBranding: e.target.checked }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-foreground">Lock Branding</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">+0.1 SOL</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Prevent others from creating similar tokens with your branding
                        </p>
                      </div>
                    </label>

                    {/* Enable Advertisements */}
                    <label className={cn(
                      "flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all",
                      form.enableAds ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                    )}>
                      <input
                        type="checkbox"
                        checked={form.enableAds}
                        onChange={(e) => setForm((f) => ({ ...f, enableAds: e.target.checked }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-foreground">Featured Ads</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">+0.05 SOL</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Display your coin in featured sections across the platform
                        </p>
                      </div>
                    </label>

                    {/* Lock Transfers */}
                    <label className={cn(
                      "flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all",
                      form.lockTransfers ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                    )}>
                      <input
                        type="checkbox"
                        checked={form.lockTransfers}
                        onChange={(e) => setForm((f) => ({ ...f, lockTransfers: e.target.checked }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-foreground">Transfer Lock</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">+0.15 SOL</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Lock transfers until target market cap is reached (anti-dump)
                        </p>
                      </div>
                    </label>

                    {/* Premium Launch */}
                    <label className={cn(
                      "flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all",
                      form.isPremium ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                    )}>
                      <input
                        type="checkbox"
                        checked={form.isPremium}
                        onChange={(e) => setForm((f) => ({ ...f, isPremium: e.target.checked }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="w-4 h-4 text-primary" />
                          <span className="font-semibold text-foreground">Premium Launch</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">+0.3 SOL</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Featured placement, verified badge, and priority support
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Fee Summary */}
                  <div className="p-4 rounded-xl bg-secondary/50 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Base Fee</span>
                      <span className="font-mono text-foreground">0.02 SOL</span>
                    </div>
                    {form.lockBranding && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Branding Lock</span>
                        <span className="font-mono text-foreground">0.1 SOL</span>
                      </div>
                    )}
                    {form.enableAds && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Featured Ads</span>
                        <span className="font-mono text-foreground">0.05 SOL</span>
                      </div>
                    )}
                    {form.lockTransfers && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Transfer Lock</span>
                        <span className="font-mono text-foreground">0.15 SOL</span>
                      </div>
                    )}
                    {form.isPremium && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Premium</span>
                        <span className="font-mono text-foreground">0.3 SOL</span>
                      </div>
                    )}
                    <div className="border-t border-border pt-2 mt-2 flex items-center justify-between">
                      <span className="font-semibold text-foreground">Total Fee</span>
                      <span className="font-mono font-bold text-primary">{totalFee.toFixed(2)} SOL</span>
                    </div>
                    <p className="text-xs text-success mt-2 text-center">
                      60% cheaper than competitors! 🚀
                    </p>
                  </div>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl bg-card border border-border">
                    <Target className="w-5 h-5 text-primary mb-2" />
                    <p className="text-sm font-medium text-foreground">Fair Launch</p>
                    <p className="text-xs text-muted-foreground">No presales or team tokens</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border">
                    <Users className="w-5 h-5 text-primary mb-2" />
                    <p className="text-sm font-medium text-foreground">1B Total Supply</p>
                    <p className="text-xs text-muted-foreground">Fixed token supply</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border">
                    <Info className="w-5 h-5 text-primary mb-2" />
                    <p className="text-sm font-medium text-foreground">0.25% Fee</p>
                    <p className="text-xs text-muted-foreground">Lowest trading fees</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card border border-border">
                    <Sparkles className="w-5 h-5 text-success mb-2" />
                    <p className="text-sm font-medium text-foreground">0.02 SOL</p>
                    <p className="text-xs text-muted-foreground">Cheapest creation</p>
                  </div>
                </div>

                {/* Submit Button */}
                {connected ? (
                  <Button type="submit" variant="hero" size="xl" className="w-full" disabled={isLoading || Object.values(errors).some(e => e !== '')}>
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Creating...
                      </span>
                    ) : (
                      <>
                        <Rocket className="w-5 h-5" />
                        Launch Coin
                      </>
                    )}
                  </Button>
                ) : (
                  <Button type="button" onClick={connect} variant="hero" size="xl" className="w-full">
                    Connect Wallet to Continue
                  </Button>
                )}
              </form>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
